import { useEffect, useRef, type CSSProperties, type HTMLAttributes, type RefObject } from "react";
import { cn } from "@/components/ui/utils";

export type AgentState = null | "thinking" | "listening" | "talking";

export interface OrbProps extends HTMLAttributes<HTMLDivElement> {
  colors?: [string, string];
  colorsRef?: RefObject<[string, string]>;
  resizeDebounce?: number;
  seed?: number;
  agentState?: AgentState;
  volumeMode?: "auto" | "manual";
  manualInput?: number;
  manualOutput?: number;
  inputVolumeRef?: RefObject<number>;
  outputVolumeRef?: RefObject<number>;
  getInputVolume?: () => number;
  getOutputVolume?: () => number;
  className?: string;
}

function mixTransparent(color: string, percent: number): string {
  if (!color) return "transparent";
  return `color-mix(in srgb, ${color} ${Math.max(0, Math.min(100, percent))}%, transparent)`;
}

function readPaintColors(el: HTMLElement): { primary: string; secondary: string } {
  const style = getComputedStyle(el);
  // Canvas 2D cannot consume `var(...)`; sample computed paint instead.
  const primary = style.color || "currentColor";
  const secondary = style.borderColor || primary;
  return { primary, secondary };
}

export function Orb({
  colors,
  agentState = null,
  volumeMode = "auto",
  manualInput = 0,
  manualOutput = 0,
  inputVolumeRef,
  outputVolumeRef,
  className,
  style,
  ...props
}: OrbProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const animFrameRef = useRef<number>(0);
  const timeRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    let ctx: CanvasRenderingContext2D | null = null;
    try {
      ctx = canvas.getContext("2d");
    } catch {
      ctx = null;
    }
    if (!ctx) return;

    let mounted = true;

    const render = () => {
      if (!mounted || !canvas || !ctx) return;

      const rect = container.getBoundingClientRect();
      const dpr = typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1;
      const width = rect.width || 128;
      const height = rect.height || 128;

      if (canvas.width !== width * dpr || canvas.height !== height * dpr) {
        canvas.width = width * dpr;
        canvas.height = height * dpr;
        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;
        ctx.scale(dpr, dpr);
      }

      ctx.clearRect(0, 0, width, height);

      const prefersReducedMotion =
        typeof window !== "undefined" &&
        window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

      timeRef.current += prefersReducedMotion
        ? 0.005
        : agentState === "thinking"
          ? 0.04
          : agentState === "talking"
            ? 0.035
            : 0.018;
      const t = timeRef.current;

      const cx = width / 2;
      const cy = height / 2;
      const baseRadius = Math.min(width, height) * 0.36;

      const inVol = inputVolumeRef?.current ?? manualInput ?? 0;
      const outVol = outputVolumeRef?.current ?? manualOutput ?? 0;
      const volBoost = prefersReducedMotion
        ? 0.02
        : agentState === "talking"
          ? Math.max(outVol, 0.28)
          : agentState === "listening"
            ? Math.max(inVol, 0.18)
            : 0.06;

      const { primary, secondary } = readPaintColors(container);

      const glowGrad = ctx.createRadialGradient(cx, cy, baseRadius * 0.1, cx, cy, baseRadius * 1.8);
      glowGrad.addColorStop(0, mixTransparent(primary, 33));
      glowGrad.addColorStop(0.5, mixTransparent(secondary, 13));
      glowGrad.addColorStop(1, "transparent");
      ctx.fillStyle = glowGrad;
      ctx.beginPath();
      ctx.arc(cx, cy, baseRadius * 1.8, 0, Math.PI * 2);
      ctx.fill();

      const gyroCount = agentState === "thinking" ? 3 : agentState ? 2 : 1;
      for (let g = 0; g < gyroCount; g++) {
        ctx.save();
        ctx.translate(cx, cy);
        const tilt = (Math.PI / 3.5) * (g + 1) + Math.sin(t * 0.6 + g) * 0.15;
        const rot = t * (0.7 + g * 0.3) * (g % 2 === 0 ? 1 : -1);
        ctx.rotate(rot);
        ctx.beginPath();
        const rx = baseRadius * (1.25 + g * 0.18) + Math.sin(t * 2 + g) * (volBoost * 6);
        const ry = rx * Math.cos(tilt);
        ctx.ellipse(0, 0, Math.max(4, rx), Math.max(2, Math.abs(ry)), 0, 0, Math.PI * 2);
        ctx.strokeStyle = mixTransparent(primary, Math.round((0.35 - g * 0.09) * 100));
        ctx.lineWidth = 1.2;
        if (g > 0) {
          ctx.setLineDash([4, 6]);
        }
        ctx.stroke();

        const pAngle = t * (1.5 + g * 0.4);
        const px = Math.cos(pAngle) * rx;
        const py = Math.sin(pAngle) * ry;
        ctx.beginPath();
        ctx.arc(px, py, 2.2, 0, Math.PI * 2);
        ctx.fillStyle = "white";
        ctx.shadowColor = primary;
        ctx.shadowBlur = 8;
        ctx.fill();
        ctx.restore();
      }

      ctx.save();
      ctx.beginPath();
      const points = 48;
      for (let i = 0; i <= points; i++) {
        const angle = (i / points) * Math.PI * 2;
        const wave =
          Math.sin(angle * 3 + t * 2.4) * (volBoost * 6) +
          Math.cos(angle * 5 - t * 1.6) * (volBoost * 4) +
          Math.sin(angle * 8 + t * 3.2) * (volBoost * 2);
        const r = baseRadius + wave;
        const x = cx + Math.cos(angle) * r;
        const y = cy + Math.sin(angle) * r;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.closePath();

      const plasmaGrad = ctx.createRadialGradient(
        cx - baseRadius * 0.28,
        cy - baseRadius * 0.28,
        baseRadius * 0.05,
        cx,
        cy,
        baseRadius * 1.05,
      );
      plasmaGrad.addColorStop(0, "white");
      plasmaGrad.addColorStop(0.18, mixTransparent(primary, 93));
      plasmaGrad.addColorStop(0.55, secondary);
      plasmaGrad.addColorStop(0.9, mixTransparent(secondary, 60));
      plasmaGrad.addColorStop(1, mixTransparent(primary, 27));

      ctx.fillStyle = plasmaGrad;
      ctx.shadowColor = primary;
      ctx.shadowBlur = 24;
      ctx.fill();
      ctx.restore();

      ctx.save();
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(cx - 1, cy - 1, baseRadius * 0.98, Math.PI * 0.7, Math.PI * 1.8);
      ctx.strokeStyle = mixTransparent(primary, 45);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(cx + 1, cy + 1, baseRadius * 0.98, Math.PI * 1.7, Math.PI * 2.8);
      ctx.strokeStyle = mixTransparent(secondary, 40);
      ctx.stroke();
      ctx.restore();

      const specGrad = ctx.createRadialGradient(
        cx - baseRadius * 0.36,
        cy - baseRadius * 0.36,
        1,
        cx - baseRadius * 0.36,
        cy - baseRadius * 0.36,
        baseRadius * 0.42,
      );
      specGrad.addColorStop(0, mixTransparent("white", 90));
      specGrad.addColorStop(0.3, mixTransparent("white", 40));
      specGrad.addColorStop(1, "transparent");
      ctx.fillStyle = specGrad;
      ctx.beginPath();
      ctx.arc(cx - baseRadius * 0.36, cy - baseRadius * 0.36, baseRadius * 0.42, 0, Math.PI * 2);
      ctx.fill();

      const rimGrad = ctx.createRadialGradient(
        cx + baseRadius * 0.3,
        cy + baseRadius * 0.3,
        baseRadius * 0.1,
        cx + baseRadius * 0.3,
        cy + baseRadius * 0.3,
        baseRadius * 0.35,
      );
      rimGrad.addColorStop(0, mixTransparent("white", 25));
      rimGrad.addColorStop(1, "transparent");
      ctx.fillStyle = rimGrad;
      ctx.beginPath();
      ctx.arc(cx + baseRadius * 0.3, cy + baseRadius * 0.3, baseRadius * 0.35, 0, Math.PI * 2);
      ctx.fill();

      animFrameRef.current = requestAnimationFrame(render);
    };

    animFrameRef.current = requestAnimationFrame(render);

    return () => {
      mounted = false;
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
      }
    };
  }, [agentState, volumeMode, manualInput, manualOutput, inputVolumeRef, outputVolumeRef]);

  const paintStyle = {
    ...(colors ? { "--orb-a": colors[0], "--orb-b": colors[1] } : {}),
    color: "var(--orb-a, var(--accent-bg))",
    borderColor: "var(--orb-b, var(--chart-2))",
    ...style,
  } as CSSProperties;

  return (
    <div
      ref={containerRef}
      className={cn(
        "relative flex size-full items-center justify-center overflow-hidden rounded-full",
        className,
      )}
      role="img"
      aria-label={agentState ? `Voice orb: ${agentState}` : "Voice orb idle"}
      style={paintStyle}
      {...props}
    >
      <span className="sr-only">
        {agentState ? `Voice assistant state: ${agentState}` : "Voice assistant idle"}
      </span>
      <div
        className="pointer-events-none absolute inset-[15%] rounded-full bg-[radial-gradient(circle,var(--orb-a,var(--accent-bg))_0%,var(--orb-b,var(--chart-2))_70%,transparent_100%)] opacity-60 blur-md transition-all duration-500"
        style={{
          transform:
            agentState === "thinking"
              ? "scale(1.1)"
              : agentState === "talking"
                ? "scale(1.15)"
                : "scale(1)",
        }}
      />
      <canvas ref={canvasRef} className="block size-full" aria-hidden="true" />
    </div>
  );
}
